import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { VEHICLE_FAQ_CATEGORIES, getVehicleCategoryDef } from "@/content/vehicle-corpus/faq-defs";

export const Route = createFileRoute("/en/vehicle/faq/$category")({
  loader: async ({ params }) => {
    const def = getVehicleCategoryDef(params.category);
    if (!def) throw notFound();
    const { getVehicleFaqsByCategoryEn } = await import("@/content/vehicle-corpus");
    return { def, faqs: getVehicleFaqsByCategoryEn(def.slug) };
  },
  component: EnVehicleFaqCategoryPage,
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Category not found" }, { name: "robots", content: "noindex" }] };
    const site = getActiveSiteUrl();
    const { def, faqs } = loaderData;
    return {
      ...pageHeadForProvince({
        title: `${def.titleEn} — ${faqs.length} FAQs`,
        description: def.descEn,
        path: `/en/vehicle/faq/${params.category}`,
        type: "article",
        alternates: [{ lang: "th", path: `/vehicle/faq/${params.category}` }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: site },
              { name: "Vehicle & Driving FAQ", url: `${site}/en/vehicle/faq` },
              { name: def.titleEn, url: `${site}/en/vehicle/faq/${params.category}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/vehicle/faq/${params.category}`)) },
      ],
    };
  },
});

function EnVehicleFaqCategoryPage() {
  const { def, faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/vehicle/faq" className="hover:underline">Vehicle &amp; Driving FAQ</a> <span>/</span>{" "}
          <span>{def.titleEn}</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">{def.titleEn}</h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">{def.descEn}</p>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section className="mt-12">
          <h2 className="mb-3 text-xl font-bold">Other topics</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {VEHICLE_FAQ_CATEGORIES.filter((c) => c.slug !== def.slug).map((c) => (
              <a key={c.slug} href={`/en/vehicle/faq/${c.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {c.titleEn}
              </a>
            ))}
          </div>
        </section>
        <FaqSourceNote topic="vehicle" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}